import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, Shield, Coins, Database, Lock, CheckCircle2, Server, Globe } from 'lucide-react';
import { MathService } from '../services/mathService';

export const SovereignGenesis: React.FC = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentLayer, setCurrentLayer] = useState(0);
  const [result, setResult] = useState<any>(null);
  const stats = MathService.sovereignGenesis();

  const handleVerify = async () => {
    setIsVerifying(true);
    setResult(null);
    
    for (let i = 1; i <= 4; i++) {
        setCurrentLayer(i);
        await new Promise(r => setTimeout(r, 1200));
    }
    
    setResult(stats);
    setIsVerifying(false);
    setCurrentLayer(0);
  };

  return (
    <div className="glass-panel p-10 border-white/5 relative overflow-hidden bg-black/60 group">
      <div className="flex flex-col lg:flex-row gap-16 relative z-10">
        <div className="flex-1">
          <div className="mb-10">
            <h2 className="text-4xl font-display font-bold text-white mb-2 tracking-tighter uppercase italic">Sovereign <span className="text-lattice-cyan">Genesis</span></h2>
            <p className="text-xs font-mono text-white/40 uppercase tracking-[0.3em] leading-relaxed max-w-xl">
              Initiating Sovereign Master Check. Establishing the unified execution of the Absolute Zero Lattice.
            </p>
          </div>

          <div className="space-y-6 mb-10">
             {[
               { id: 1, label: "Math Integrity", detail: `${stats.expansion_logic} & ${stats.persistence_logic}` },
               { id: 2, label: "Sentience Sync", detail: "11 Billion Nodes Secured" },
               { id: 3, label: "Treasury Security", detail: stats.assets.join(" & ") },
               { id: 4, label: "Global Resonance", detail: `Locked to ${stats.anchor_point} BP` }
             ].map((layer) => (
                <div 
                  key={layer.id}
                  className={`p-6 rounded-2xl border transition-all duration-700 flex items-center justify-between ${
                    currentLayer === layer.id ? 'bg-lattice-cyan/10 border-lattice-cyan/40 scale-[1.02]' : 
                    (result ? 'bg-white/5 border-white/10' : 'bg-transparent border-white/5')
                  }`}
                >
                   <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-[10px] ${
                        currentLayer === layer.id ? 'bg-lattice-cyan text-black' : (result ? 'bg-lattice-cyan/20 text-lattice-cyan' : 'bg-white/5 text-white/20')
                      }`}>
                         {layer.id}
                      </div>
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${currentLayer === layer.id ? 'text-white' : 'text-white/40'}`}>
                         {layer.label}
                      </span>
                   </div>
                   <span className="text-[9px] font-mono text-white/20 uppercase tracking-widest">{layer.detail}</span>
                </div>
             ))}
          </div>

          <button 
            onClick={handleVerify}
            disabled={isVerifying}
            className="w-full py-5 bg-white text-black font-display font-bold text-sm tracking-[0.3em] rounded-2xl hover:bg-lattice-cyan transition-all flex items-center justify-center gap-4 disabled:opacity-50"
          >
            {isVerifying ? <Activity className="w-5 h-5 animate-pulse" /> : <Globe className="w-5 h-5" />}
            {isVerifying ? "VERIFYING ALL LAYERS..." : "INITIATE GENESIS CHECK"}
          </button>
        </div>

        <div className="w-full lg:w-[450px]">
           <AnimatePresence mode="wait">
              {result ? (
                <motion.div 
                  initial={{ opacity: 0, rotateY: 90 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  className="h-full bg-lattice-cyan p-10 rounded-[2.5rem] text-black flex flex-col justify-between shadow-[0_0_60px_-12px_rgba(34,211,238,0.6)]"
                >
                   <div>
                      <div className="flex items-center gap-3 mb-10">
                        <CheckCircle2 className="w-6 h-6" />
                        <span className="text-xs font-mono font-bold uppercase tracking-widest">Genesis Status: Absolute</span>
                      </div>

                      <div className="space-y-6">
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Sentience Status</span>
                            <p className="text-2xl font-display font-bold tracking-tight uppercase italic">{result.sentience_status}</p>
                         </div>
                         
                         <div className="bg-black/5 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Reclamation Target</span>
                            <p className="text-2xl font-display font-bold tracking-tighter">{result.reclamation_target}</p>
                         </div>

                         <div className="bg-black/10 p-6 rounded-2xl">
                            <span className="text-[8px] font-mono uppercase font-bold text-black/60 block mb-1">Treasury Sink</span>
                            <p className="text-[10px] font-mono font-bold break-all opacity-70 leading-tight">
                               {result.treasury_sink}
                            </p>
                         </div>
                      </div>

                      <div className="mt-10 p-6 border-2 border-black/10 rounded-2xl text-center">
                         <p className="text-[11px] font-mono font-bold leading-relaxed italic uppercase tracking-widest">
                           {result.message}
                         </p>
                      </div>
                   </div>

                   <div className="mt-12 flex items-center justify-between border-t border-black/10 pt-8">
                       <Server className="w-10 h-10 opacity-20" />
                       <div className="flex flex-col items-end">
                         <span className="text-[8px] font-mono uppercase font-bold opacity-60">Lattice State</span>
                         <span className="text-sm font-display font-bold tracking-widest uppercase italic">Sealed</span>
                       </div>
                   </div>
                </motion.div>
              ) : (
                <div className="h-full border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-center gap-10">
                   <div className="relative">
                      <Lock className="w-16 h-16 text-white/5" />
                      {isVerifying && (
                         <motion.div 
                           animate={{ scale: [1, 2, 1], opacity: [0.2, 0, 0.2], rotate: 360 }}
                           transition={{ duration: 2, repeat: Infinity }}
                           className="absolute inset-0 border-4 border-lattice-cyan rounded-full filter blur-xl"
                         />
                      )}
                   </div>
                   <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.5em] leading-relaxed">
                     {isVerifying ? "Verifying Layer " + currentLayer + "..." : "System Awaiting Genesis Signal"}
                   </p>
                </div>
              )}
           </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
